import { FC, useEffect, useRef } from 'react'
import { useSnackbar, SnackbarKey } from 'notistack'
import { IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { AttestationWarning } from '@/services/ruutsApi/getAttestationAreas'

type Props = {
  warnings: AttestationWarning[]
}

const dedupKey = (w: AttestationWarning) =>
  `${w.code}:${w.details.carbonInstance.id}:${w.details.overlapWith.id ?? w.details.overlapWith.layer ?? w.details.overlapWith.name}`

const buildMessage = (w: AttestationWarning): string => {
  const ci = w.details.carbonInstance.name
  const target = w.details.overlapWith.name
  const ha = w.overlapHa.toFixed(2)
  switch (w.code) {
    case 'CI_OVERLAP_CI':
      return `La instancia "${ci}" se superpone con la instancia "${target}" en ${ha} ha`
    case 'CI_OVERLAP_EA':
      return `La instancia "${ci}" se superpone con el área excluida "${target}" en ${ha} ha`
    case 'CI_OVERLAP_INELIGIBILITY':
      return `La instancia "${ci}" se superpone con "${target}" (capa no elegible) en ${ha} ha`
    default:
      return `Superposición detectada en la instancia "${ci}" (${ha} ha)`
  }
}

type DismissButtonProps = {
  snackbarId: SnackbarKey
  onClose: (key: SnackbarKey) => void
}

const DismissSnackbarButton: FC<DismissButtonProps> = ({ snackbarId, onClose }) => (
  <IconButton aria-label="Cerrar" color="inherit" size="small" onClick={() => onClose(snackbarId)}>
    <CloseIcon fontSize="small" />
  </IconButton>
)

const renderDismissAction = (
  closeSnackbar: (key: SnackbarKey) => void,
  snackbarId: SnackbarKey,
): JSX.Element => <DismissSnackbarButton snackbarId={snackbarId} onClose={closeSnackbar} />

export const AttestationWarningsToast: FC<Props> = ({ warnings }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const shownKeysRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    warnings.forEach(w => {
      const key = dedupKey(w)
      if (shownKeysRef.current.has(key)) return
      shownKeysRef.current.add(key)
      enqueueSnackbar(buildMessage(w), {
        variant: 'warning',
        persist: true,
        preventDuplicate: true,
        action: renderDismissAction.bind(null, closeSnackbar),
      })
    })
  }, [warnings, enqueueSnackbar, closeSnackbar])

  return null
}
